import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AppBar, Box, Button, IconButton, Menu, MenuItem, ListItemText, Toolbar, Typography, useMediaQuery } from '@mui/material';
import { Logout as LogoutIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../../../context/AuthContext';
import { useNavigationStack } from '../../../../context/NavigationStackContext';
import OrbitLogo from '../../../../components/common/OrbitLogo';
import ProfilePicture from '../../../../components/common/ProfilePicture';

function HeaderBar({ children }) {
  return (
    <AppBar
      position="static"
      elevation={0}
      sx={{ backgroundColor: 'background.default', borderBottom: 1, borderColor: 'divider' }}
    >
      <Toolbar sx={{ gap: 1 }}>
        <OrbitLogo height={24} />
        <span style={{ flex: 1 }} />
        {children}
      </Toolbar>
    </AppBar>
  );
}

/**
 * Header on a scanner phone: who is booking, and one obvious way out.
 * `who` is the staff member's name, or the scanner's name without staff login.
 */
export function DeviceScannerHeader({ who, user, onLogout, logoutLabel }) {
  // On the narrowest phones the label would squeeze the name to a few letters
  const iconOnly = useMediaQuery('(max-width:359px)');
  return (
    <HeaderBar>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
        {user && (
          <ProfilePicture profilePictureFileId={user.profile_picture_file_id} name={user.name} size={28} color={user.color} />
        )}
        <Typography variant="body2" fontWeight={600} color="text.primary" noWrap data-testid="scanner-who">
          {who}
        </Typography>
      </Box>
      {iconOnly ? (
        <IconButton onClick={onLogout} aria-label={logoutLabel} sx={{ width: 44, height: 44, border: 1, borderColor: 'divider', borderRadius: 3, flexShrink: 0 }}>
          <LogoutIcon />
        </IconButton>
      ) : (
        <Button
          variant="outlined"
          color="inherit"
          startIcon={<LogoutIcon />}
          onClick={onLogout}
          sx={{ minHeight: 44, color: 'text.primary', borderColor: 'divider', flexShrink: 0 }}
        >
          {logoutLabel}
        </Button>
      )}
    </HeaderBar>
  );
}

// Header for a staff member who opened the scanner from the normal app
export default function ScannerHeader() {
  const { t } = useTranslation('common');
  const { user } = useAuth();
  const navigate = useNavigate();
  const { clearStack } = useNavigationStack();
  const [anchor, setAnchor] = useState(null);

  const handleSwitchUser = () => {
    setAnchor(null);
    clearStack();
    navigate('/auth/change-user');
  };

  return (
    <HeaderBar>
      <IconButton size="small" onClick={e => setAnchor(e.currentTarget)}>
        <ProfilePicture
          profilePictureFileId={user?.profile_picture_file_id}
          name={user?.name}
          size={32}
          color={user?.color}
        />
      </IconButton>
      <Menu
        anchorEl={anchor}
        open={Boolean(anchor)}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <MenuItem onClick={() => { setAnchor(null); navigate('/home'); }}>
          <ListItemText>{t('common:navigation.home')}</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleSwitchUser}>
          <ListItemText>{t('common:user_menu.logout')}</ListItemText>
        </MenuItem>
      </Menu>
    </HeaderBar>
  );
}
